/**
 * Una investigación en el listado del inicio, con sus acciones.
 *
 * Borrar es en dos escalones deliberadamente. Una investigación no es una
 * fila cualquiera: representa minutos de espera y dinero ya gastado en el
 * proveedor —Cerro Colorado lleva 4,83 USD en seis corridas—, y nada de eso
 * se recupera. Así que «Descartar» solo la esconde y es reversible, y el
 * borrado permanente vive detrás de esa pantalla, diciendo en números qué se
 * va a destruir.
 */

import { ChevronRight, RotateCcw, Trash2, X } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useBorrarInvestigacion,
  useDescartarInvestigacion,
  useRestaurarInvestigacion,
} from '../api/hooks'
import type { Entidad } from '../api/tipos'
import { Confirmacion } from '../ui/Dialogo'
import { mensajeDeError, useAvisos } from '../ui/avisos-contexto'
import { cx, formatearCosto } from '../ui/util'

export function FilaInvestigacion({ entidad }: { entidad: Entidad }) {
  const navegar = useNavigate()
  const avisos = useAvisos()
  const descartar = useDescartarInvestigacion()
  const restaurar = useRestaurarInvestigacion()
  const borrar = useBorrarInvestigacion()

  const [confirmando, setConfirmando] = useState<'descartar' | 'borrar' | null>(null)

  const descartada = entidad.estado === 'DESCARTADO'

  return (
    <li
      className={cx(
        'flex items-center gap-1 pr-2 hover:bg-[var(--color-acento-suave)]',
        descartada && 'opacity-60',
      )}
    >
      <button
        onClick={() => navegar(`/entidad/${entidad.id}`)}
        className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span
              className={cx('truncate text-[13px] font-medium', descartada && 'line-through')}
            >
              {entidad.nombre}
            </span>
            {descartada && (
              <span className="shrink-0 rounded-full border border-[var(--color-borde-fuerte)] px-1.5 text-[10px] text-[var(--color-texto-tenue)]">
                Descartada
              </span>
            )}
            {entidad.n_duplicados_pendientes > 0 && !descartada && (
              <span className="shrink-0 rounded-full bg-[var(--color-critica)] px-1.5 text-[10px] font-semibold text-white">
                {entidad.n_duplicados_pendientes} por revisar
              </span>
            )}
          </div>
          <div className="mt-0.5 truncate text-[12px] text-[var(--color-texto-tenue)]">
            {[entidad.sector, entidad.region, entidad.pais].filter(Boolean).join(' · ') ||
              'Sin datos de contexto'}
          </div>
        </div>
        <span className="tabular shrink-0 text-[12px] text-[var(--color-texto-suave)]">
          {entidad.n_stakeholders} stakeholders
        </span>
        <ChevronRight size={15} className="shrink-0 text-[var(--color-texto-tenue)]" />
      </button>

      <div className="flex shrink-0 items-center gap-0.5">
        {descartada ? (
          <>
            <BotonIcono
              titulo="Restaurar"
              onClick={() =>
                restaurar.mutate(entidad.id, {
                  onError: (e) => avisos.error('No se pudo restaurar', mensajeDeError(e)),
                })
              }
            >
              <RotateCcw size={14} />
            </BotonIcono>
            <BotonIcono
              titulo="Borrar para siempre"
              peligro
              onClick={() => setConfirmando('borrar')}
            >
              <Trash2 size={14} />
            </BotonIcono>
          </>
        ) : (
          <BotonIcono titulo="Descartar" onClick={() => setConfirmando('descartar')}>
            <X size={15} />
          </BotonIcono>
        )}
      </div>

      <Confirmacion
        abierto={confirmando === 'descartar'}
        onCerrar={() => setConfirmando(null)}
        etiquetaConfirmar="Descartar"
        cargando={descartar.isPending}
        titulo={`Descartar «${entidad.nombre}»`}
        descripcion="Esta investigación se archivará y se eliminará del listado. Por favor, confirma la acción."
        onConfirmar={() =>
          descartar.mutate(entidad.id, {
            onSuccess: () => {
              setConfirmando(null)
              // El aviso trae el deshacer: es el camino de vuelta más corto
              // si el clic fue un error.
              avisos.exito('Investigación descartada', {
                detalle: `«${entidad.nombre}» ya no aparece en el listado.`,
                deshacer: {
                  etiqueta: 'Restaurar',
                  fn: () => restaurar.mutate(entidad.id),
                },
              })
            },
            onError: (e) => {
              setConfirmando(null)
              avisos.error('No se pudo descartar', mensajeDeError(e))
            },
          })
        }
      />

      <Confirmacion
        abierto={confirmando === 'borrar'}
        onCerrar={() => setConfirmando(null)}
        etiquetaConfirmar="Borrar para siempre"
        peligro
        cargando={borrar.isPending}
        titulo={`Borrar «${entidad.nombre}»`}
        descripcion="Esto no se puede deshacer. Se destruye todo lo investigado, y volver a obtenerlo cuesta otra corrida."
        detalle={
          <ul className="space-y-0.5">
            <li>{entidad.n_stakeholders} stakeholders con sus relaciones y fuentes</li>
            <li>
              {entidad.n_corridas} {entidad.n_corridas === 1 ? 'corrida' : 'corridas'} del
              historial
              {entidad.costo_total_usd > 0 && (
                <> · {formatearCosto(entidad.costo_total_usd)} ya gastados</>
              )}
            </li>
          </ul>
        }
        onConfirmar={() =>
          borrar.mutate(entidad.id, {
            onSuccess: () => {
              setConfirmando(null)
              avisos.exito('Investigación borrada', {
                detalle: `«${entidad.nombre}» y todo lo que contenía ya no existen.`,
              })
            },
            onError: (e) => {
              setConfirmando(null)
              avisos.error('No se pudo borrar', mensajeDeError(e))
            },
          })
        }
      />
    </li>
  )
}

function BotonIcono({
  titulo,
  peligro,
  onClick,
  children,
}: {
  titulo: string
  peligro?: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      title={titulo}
      aria-label={titulo}
      onClick={onClick}
      className={cx(
        'rounded p-1.5 text-[var(--color-texto-tenue)] hover:bg-[var(--color-panel)]',
        peligro ? 'hover:text-[var(--color-opositora)]' : 'hover:text-[var(--color-texto)]',
      )}
    >
      {children}
    </button>
  )
}
